import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — la analítica del portal, `/api/analytics`. Módulo 14, opción 2.
 *
 * Las seis llamadas del vanilla, sin cambios: un tablero de Bienestar, un
 * resumen general y dos pares de resumen + tendencia, uno de Aprendizaje y otro
 * de Cultura.
 *
 * **Cada pestaña pide lo suyo y solo cuando está abierta.** Es lo mismo que
 * hacía el vanilla; la diferencia es que aquí, mientras no se cambie de
 * dirección, volver a una pestaña ya vista no vuelve a preguntar.
 */

export type Pestana = 'bienestar' | 'general' | 'aprendizaje' | 'cultura';

/** La cadena que el servidor pone en cada alerta del tablero para decir a qué
 *  pantalla lleva (`induction`, `tramites`, `encuestas`, `kudos`, `cursos`,
 *  `celebraciones`). Se usa tal cual como hash. */
export interface AlertaDelTablero {
  icon: string;
  sev: 'danger' | 'warn' | 'info';
  page: string;
  title: string;
  detail: string;
}

export interface Bienestar {
  scope: string;
  resumen: {
    usuarios_activos: number;
    induccion_al_dia_pct: number;
    induccion_pendiente: number;
    clima_participacion_pct: number;
    kudos_mes: number;
    solicitudes_pendientes: number;
    eventos_proximos: number;
  };
  clima: { survey: string | null; participacion_pct: number; respondieron: number; total: number };
  alertas: AlertaDelTablero[];
  por_area: Array<{ area: string; usuarios: number; induccion_pct: number }>;
}

export interface General {
  usuarios_activos: number;
  usuarios_inactivos: number;
  areas: number;
  publicaciones: number;
  comentarios: number;
  reacciones: number;
  reportes_pendientes: number;
  cursos: number;
  inscripciones: number;
  cursos_completados: number;
  certificados_vigentes: number;
  eventos_total: number;
  eventos_proximos: number;
  inscripciones_eventos: number;
  encuestas_activas: number;
  respuestas_encuestas: number;
  kudos: number;
  comunidades: number;
  miembros_comunidades: number;
  solicitudes_total: number;
  solicitudes_por_estado: Record<string, number>;
}

export interface Alerta {
  severity: 'high' | 'medium' | 'ok' | string;
  text: string;
}

export interface Aprendizaje {
  overview: {
    courses_published: number;
    active_users: number;
    total_enrollments: number;
    completed: number;
    in_progress: number;
    completion_rate: number;
    approval_rate: number;
    avg_attempts: number;
    cert_summary: { vigente: number; por_vencer: number; vencido: number; total: number };
    mandatory_compliance: number | null;
    learning_paths: number;
    competencies: number;
  };
  by_area: Array<{
    area: string; enrollments: number; completed: number;
    completion_rate: number; avg_progress: number;
  }>;
  top_courses: Array<{ id: number; title: string; enrollments: number; completion_rate: number }>;
  alerts: Alerta[];
}

export interface Cultura {
  overview: {
    active_users: number;
    participating_users: number;
    participation_rate: number;
    total_posts: number;
    total_comments: number;
    total_reactions: number;
    total_kudos: number;
    communities: number;
    celebrations_attended: number;
    pending_reports: number;
  };
  posts_by_type: Array<{ label: string; count: number }>;
  by_area: Array<{ area: string; interactions: number; avg: number }>;
  top_values: Array<{ name: string; icon: string; count: number }>;
  alerts: Alerta[];
}

export interface Tendencia<T> {
  series: T[];
}

export type MesDeAprendizaje = { month: string; label: string; enrollments: number; completions: number };
export type MesDeCultura = { month: string; label: string; posts: number; kudos: number };

export const claves = {
  todo: ['analitica'] as const,
  bienestar: ['analitica', 'bienestar'] as const,
  general: ['analitica', 'general'] as const,
  aprendizaje: ['analitica', 'aprendizaje'] as const,
  aprendizajeTendencia: ['analitica', 'aprendizaje', 'tendencia'] as const,
  cultura: ['analitica', 'cultura'] as const,
  culturaTendencia: ['analitica', 'cultura', 'tendencia'] as const,
};

export function useBienestar(activa: boolean) {
  return useQuery({
    queryKey: claves.bienestar,
    queryFn: () => api.get<Bienestar>('/analytics/bienestar'),
    enabled: activa,
  });
}

export function useGeneral(activa: boolean) {
  return useQuery({
    queryKey: claves.general,
    queryFn: () => api.get<General>('/analytics/overview'),
    enabled: activa,
  });
}

export function useAprendizaje(activa: boolean) {
  return useQuery({
    queryKey: claves.aprendizaje,
    queryFn: () => api.get<Aprendizaje>('/analytics/learning/summary'),
    enabled: activa,
  });
}

export function useTendenciaDeAprendizaje(activa: boolean) {
  return useQuery({
    queryKey: claves.aprendizajeTendencia,
    queryFn: () => api.get<Tendencia<MesDeAprendizaje>>('/analytics/learning/trends'),
    enabled: activa,
  });
}

export function useCultura(activa: boolean) {
  return useQuery({
    queryKey: claves.cultura,
    queryFn: () => api.get<Cultura>('/analytics/culture/summary'),
    enabled: activa,
  });
}

export function useTendenciaDeCultura(activa: boolean) {
  return useQuery({
    queryKey: claves.culturaTendencia,
    queryFn: () => api.get<Tendencia<MesDeCultura>>('/analytics/culture/trends'),
    enabled: activa,
  });
}

/** Cómo se lee un porcentaje: bueno, a medias o corto.
 *
 *  Devuelve un ESTADO y no un color, que es la decisión que el vanilla ya había
 *  tomado y conviene conservar: treinta y un indicadores de colores decorativos
 *  distintos no dejaban usar el color para saber dónde mirar. */
export function estadoDelPorcentaje(
  pct: number | null | undefined, bueno = 80, alerta = 50,
): 'bueno' | 'alerta' | 'malo' | null {
  if (pct == null) return null;
  if (pct >= bueno) return 'bueno';
  if (pct >= alerta) return 'alerta';
  return 'malo';
}
